/** The arena's games, in display order. One list feeds the home posters, the menu sheet and the layout. */
import type { ChallengeGame, GameId, QuestMetric } from '@xox/shared'

export interface GameInfo {
  to: '/xox' | '/ice' | '/c4' | '/cycles' | '/dungeon'
  title: string
  /** Three-beat hook shown under the title. */
  tagline: string
  /** Short facts shown as chips. */
  chips: readonly string[]
}

export const GAMES: readonly GameInfo[] = [
  {
    to: '/xox',
    title: 'Vanishing XOX',
    tagline: 'Erase. Outsmart. Win.',
    chips: ['2 players', 'Boss: XOX-9000', 'No draws'],
  },
  {
    to: '/c4',
    title: 'Connect Four',
    tagline: 'Drop. Stack. Connect.',
    chips: ['2 players', 'Boss: DROP-9000', '7 × 6 board'],
  },
  {
    to: '/cycles',
    title: 'Light Cycles',
    tagline: 'Turn. Trap. Survive.',
    chips: ['2–8 players', 'Bots: GRID-9000', 'Real-time'],
  },
  {
    to: '/dungeon',
    title: 'Daily Dungeon',
    tagline: 'Delve. Fight. Return.',
    chips: ['Solo', 'New map every day', '10 floors'],
  },
  {
    to: '/ice',
    title: 'Ice Ice Baby',
    tagline: 'Slide. Dodge. Escape.',
    chips: ['100 levels', 'Race up to 8', 'Solo or party'],
  },
]

/** Where each multiplayer game's rooms live in the URL (challenge cards land players here). */
export const ROOM_PATH_FOR = {
  xox: '/r',
  c4: '/c4/r',
  cycles: '/cycles/r',
  ice: '/ice/r',
} as const satisfies Record<ChallengeGame, string>

/** The duels' room paths; the arena home links resumable rooms through this. */
export const ROOM_PATHS: Record<GameId, '/r' | '/c4/r'> = {
  xox: ROOM_PATH_FOR.xox,
  c4: ROOM_PATH_FOR.c4,
}

/** The poster entry of each game a player can be challenged to. */
export const GAME_BY_CHALLENGE: Record<ChallengeGame, GameInfo> = {
  xox: gameAt('/xox'),
  c4: gameAt('/c4'),
  cycles: gameAt('/cycles'),
  ice: gameAt('/ice'),
}

function gameAt(to: GameInfo['to']): GameInfo {
  const g = GAMES.find((g) => g.to === to)
  if (!g) throw new Error(`no game at ${to}`)
  return g
}

/** Where a quest sends you: the home of the game that advances it (duel quests go to XOX). */
export const QUEST_HOME: Record<QuestMetric, GameInfo['to']> = {
  dungeonFloor: '/dungeon',
  dungeonKills: '/dungeon',
  dungeonRun: '/dungeon',
  duelPlayed: '/xox',
  bossWin: '/xox',
  duelWin: '/xox',
  cyclesMatch: '/cycles',
  cyclesWin: '/cycles',
  iceRaced: '/ice',
  iceFirst: '/ice',
}

/** A game's home page (used by the picker and the menu sheet). */
export function isGameHome(pathname: string): boolean {
  return GAMES.some((g) => g.to === trim(pathname))
}

/** Other pages that carry their own phone bottom bar. */
const PHONE_NAV_PAGES = ['/xox/wall', '/c4/wall'] as const

/** Pages with a phone bottom bar get the slim header (logo + sound only). */
export function hasPhoneNav(pathname: string): boolean {
  const p = trim(pathname)
  return isGameHome(p) || PHONE_NAV_PAGES.some((page) => page === p)
}

function trim(pathname: string): string {
  return pathname.replace(/\/+$/, '')
}
